import Video from './Video';
import videoType from 'constants/videoType';
import { deepMerge } from 'toolbox/deepMerge';
import { on, off } from 'toolbox/event';

/**
 * This is a description of the FlowplayerVideo constructor function.
 * @class
 * @classdesc This is a description of the FlowplayerVideo class. (must be edited)
 * @extends Component
 */
export default class FlowplayerVideo extends Video {
    /**
     * Constructor of the class that mainly merge the options of the components
     * @param {HTMLElement} element HTMLElement of the component
     * @param {object} options options that belongs to the component
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            type: videoType.FLOWPLAYER,
            width: '100%',
            height: '100%',
            videoID: null,
            autoplay: 0,
            autopause: true, // Whether a player should autopause when it is outside of the viewport
            mute: false,
            loop: 1,
            controls: 1,
            startAt: null,
            siteId: null,
            autoVideoRatio: null,
            useFlowplayerModule: false, // Whether to use the Flowplayer API as a module
        }, options));
        this.API_URL = 'https://cdn.flowplayer.com/players/<siteid>/native/flowplayer.async.js';
        this.MODULE_URL = '//embed.flowplayer.com/<siteid>/<videoid>.js';
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonyous function
     */
    bindEvents() {
        super.bindEvents();
        on('moduleimported', this.element, this.onFlowPlayerModule.bind(this));
    }

    /**
     * Handles the moduleimported event and loads the player
     * @returns {void}
     */
    onFlowPlayerModule() {
        this.loadPlayer();
    }

    /**
     * Renders the HTML5 video player and then plays the video
     *
     * @param {Object} event - Event object
     */
    startVideo(event) {
        if (!this.state.playOnRender) {
            this.options.autoplay = true;
            this.state.playOnRender = true;
            this.createVideo(event);
        }
    }

    /**
     * Loads Flowplayer API to render video inside component and call loadPlayer() when API ready.
     *
     * @param {Object} event - Event object
     */
    createVideo(event) {
        if (event) {
            event.preventDefault();
        }
        this.addLoader();

        if (!this.options.videoId) {
            throw new Error('The videoId of the player is missing!');
        }

        if (!this.options.siteId) {
            throw new Error('The siteId of the player is missing!');
        }

        if (!this.options.useFlowplayerModule) {
            // Load the IFrame Player API code asynchronously.
            FlowplayerVideo.loadAPI(this.API_URL.replace('<siteid>', this.options.siteId))
                .then(this.loadPlayer.bind(this))
                .catch((e) => {
                    console.error(`Flowplayer load API promise rejected : ${e}`);
                });

            return;
        }
        // Import the embedding script module to bypass the advertising functionality
        this.importAPI(this.MODULE_URL.replace('<siteid>', this.options.siteId).replace('<videoid>', this.options.videoId));
    }

    /**
     * Imports the Flowplayer API script and sets the window.flowplayer object
     * @param {string} url - the URL of the Flowplayer API script to import
     * @returns {void}
     */
    importAPI(url) {
        return new Promise((resolve) => {
            const componentId = this.element.getAttribute('data-component-id');

            if (FlowplayerVideo.isAPILoaded()) {
                document.querySelector(`[data-component-id="${componentId}"]`).dispatchEvent(new Event('moduleimported'));
                resolve();
                return;
            }

            const importScriptTag = document.createElement('script');
            const firstScriptTag = document.head.getElementsByTagName('script')[0];

            importScriptTag.type = 'module';
            importScriptTag.text = `
            import('${url}').then((module) => {
                window.flowplayer = module.default;
                document.querySelector('[data-component-id="${componentId}"]').dispatchEvent(new Event('moduleimported'));
            })`;
            firstScriptTag.parentNode.insertBefore(importScriptTag, firstScriptTag);
        });
    }

    /**
     * Returns true if Flowplayer API is already loaded.
     *
     * @return {Boolean} true or false
     */
    static isAPILoaded() {
        return !!window.flowplayer;
    }

    /**
     * Loads Flowplayer API and returns promise
     *
     * @param {Object} url - Event object
     * @returns {Promise} object
     */
    static loadAPI(url) {
        return new Promise((resolve, reject) => {
            if (FlowplayerVideo.isAPILoaded()) {
                resolve();
                return;
            }

            // Load the IFrame Player API code asynchronously.
            const apiScriptTag = document.createElement('script');
            apiScriptTag.src = url;
            apiScriptTag.async = true;
            apiScriptTag.onload = resolve;
            apiScriptTag.onerror = reject;
            const firstScriptTag = document.head.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(apiScriptTag, firstScriptTag);
        });
    }

    /**
     * return component video player
     * @returns {Object} player
     */
    getPlayer() {
        return this.player;
    }

    /**
     * Calls Flowplayer API to create video player iframe
     *
     * @param {String} playerId - id of element, which will be relaced by video player
     * @return {Object} window.Flowplayer.Player
     */
    createPlayer(playerId) {
        if (this.selectors.videoPlayer) {
            return this.selectors.videoPlayer;
        }

        const playerVars = {
            autoplay: this.options.autoplay,
            loop: this.options.loop,
            muted: this.options.mute,
            autopause: this.options.autopause,
            controls: +!!this.options.controls,
        };

        if (!this.options.useFlowplayerModule) {
            playerVars.src = this.options.videoId;
        }

        playerVars.ui = 0;
        if (this.options.controls === 0) {
            const flowplayerUINoControls = 1024;
            const flowplayerUINoFullscreen = 1;
            const flowplayerUINoTouchZones = 16384;
            // eslint-disable-next-line no-bitwise
            playerVars.ui = flowplayerUINoControls | flowplayerUINoFullscreen | flowplayerUINoTouchZones;
        }
        const flowplayerUINoTitle = 256;
        const flowplayerUINoDesc = 512;
        // eslint-disable-next-line no-bitwise
        playerVars.ui += flowplayerUINoTitle | flowplayerUINoDesc;

        const videoassetlink = document.getElementById(playerId);
        if (videoassetlink) {
            videoassetlink.remove();
        }

        this.state.playerId = playerId;
        const flowplayerElement = document.createElement('div');
        flowplayerElement.id = playerId;
        this.element.appendChild(flowplayerElement);
        window.flowplayer(`#${this.state.playerId}`, playerVars);

        if (!this.options.useFlowplayerModule) {
            window.flowplayer.cloud.then(this.onRenderVideo.bind(this, true));
        }
        this.selectors.videoPlayer = window.flowplayer(`#${this.state.playerId}`);

        return this.selectors.videoPlayer;
    }

    /**
     * Configure video player and bind player events.
     */
    onPlayerReady() {
        this.selectors.videoPlayer = window.flowplayer(`#${this.state.playerId}`);

        if (this.options.autoVideoRatio) {
            this.element.classList.add('m-auto-ratio');
        }
        this.handlePlayerReadyOptions();

        this.selectors.videoPlayer.classList.add('disable-play');

        this.selectors.videoPlayer.on('playing', this.onPlayVideo.bind(this));
        this.selectors.videoPlayer.on('ended', this.onStopVideo.bind(this));
        this.selectors.videoPlayer.on('pause', this.onPauseVideo.bind(this));
        this.selectors.videoPlayer.on('canplay', this.onCanPlayVideo.bind(this));
    }

    /**
     * Play video
     * Here the logic is removed to not disturb the flowplayer behavior already playing the video by himself
     */
    onPlayerPlay() {
        // overloading the method from the parent class Video.js
    }

    /**
     * Pause video
     * Here the logic is removed to not disturb the flowplayer behavior already pausing the video by himself
     */
    onPlayerPause() {
        // overloading the method from the parent class Video.js
    }

    /**
     * Mute the video
     */
    mute() {
        this.selectors.videoPlayer.toggleMute();
    }

    /**
     * Unmute the video
     */
    unMute() {
        this.selectors.videoPlayer.toggleMute();
    }

    /**
     * Mute video player
     */
    muteVideoPlayer() {
        this.mute();
    }

    /**
     * Unmute video player
     */
    unMuteVideoPlayer() {
        this.unMute();
    }

    /**
     * Play video when it is playable
     * Flowplayer will play the video when it is ready
     */
    onCanPlayVideo() {
        this.addObserver(this.selectors.videoPlayer);
        if (this.state.playOnRender) {
            this.playVideoPlayer();
        }
    }

    /**
     * Send event analytics
     * @param {String} action - action value
     */
    sendEventAnalytics(action) {
        this.sendAnalytics({
            event: 'uaevent',
            category: 'video',
            action,
            label: this.options.videoId,
            extraData: {
                event_name: 'video_interaction',
                video_title: this.options.videoId || this.selectors.videoPlayer.currentSrc,
                video_action: action,
            },
        });
    }

    /**
     * Update player progress in percents, report the % played if it matches some of the breakpoints
     */
    sendProgressToAnalytics() {
        const percent = this.selectors.videoPlayer.currentTime / this.selectors.videoPlayer.duration;

        const prevBreakpoint = this.getProgressBreakpoint(this.state.progressPercentage);
        const currentBreakpoint = this.getProgressBreakpoint(percent);

        this.state.progressPercentage = percent;

        if (prevBreakpoint < currentBreakpoint) {
            const percentLabel = (currentBreakpoint * 100).toFixed();
            this.sendEventAnalytics(`${percentLabel}% Viewed`);
        }
    }

    /**
     * Play video player
     */
    playVideoPlayer() {
        if (this.selectors.videoPlayer) {
            this.state.paused = false;
            this.selectors.videoPlayer.play();
        }
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        // destroy the video player instance
        if (this.selectors.videoPlayer) {
            this.clearPercentageInteval();
            this.selectors.videoPlayer.destroy();
            this.selectors.videoPlayer = null;
        }

        super.destroy();
        off('moduleimported', this.element);
    }
}
